import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheckIcon as ShieldCheck, TrashIcon as Trash } from "@phosphor-icons/react";

import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RowListSkeleton } from "@/components/SkeletonList";
import { formatApiError } from "@/lib/utils-app";
import VerifiedStandardsBadge from "@/components/VerifiedStandardsBadge";

/**
 * Self-attestation flow for the Verified Standards badge.
 *
 * The seller must explicitly tick every item in the current standards
 * version to earn the badge. We show all 15 items as required
 * checkboxes with a sticky submit footer at the bottom that stays
 * disabled until everything is acknowledged.
 */
export default function SellerStandardsAttest() {
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [my, setMy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ack, setAck] = useState(new Set());
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        api.get("/seller-standards/current"),
        api.get("/seller-standards/my-attestation"),
      ]);
      setDoc(c.data);
      setMy(m.data);
      if (m.data?.attested) {
        setAck(new Set(m.data.attestation?.acknowledged_items || []));
        setName(m.data.attestation?.public_display_name || "");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const allItems = useMemo(() => {
    return (doc?.sections || []).flatMap((s) => s.items || []);
  }, [doc]);
  const totalItems = allItems.length;
  const allChecked = totalItems > 0 && allItems.every((i) => ack.has(i.id));

  const toggle = (id) => {
    setAck((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const tickAll = () => setAck(new Set(allItems.map((i) => i.id)));
  const untickAll = () => setAck(new Set());

  const submit = async () => {
    if (!allChecked) {
      toast.error(`Please tick all ${totalItems} items to earn the badge.`);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/seller-standards/attest", {
        acknowledged_items: Array.from(ack),
        public_display_name: name.trim() || undefined,
      });
      toast.success("Verified Standards badge earned");
      navigate("/seller-standards");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!confirm("Remove your Verified Standards badge? You can re-attest at any time.")) return;
    try {
      await api.post("/seller-standards/withdraw");
      toast.success("Badge removed");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <AppShell>
      <article className="max-w-3xl mx-auto pb-32" data-testid="seller-standards-attest-page">
        <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.18em] text-gold font-bold">
          <ShieldCheck size={12} weight="fill" />
          Verified standards · attestation
        </div>
        <h1 className="font-spectral text-4xl tracking-tight text-ink mb-3">
          Earn the badge
        </h1>
        <p className="text-[14px] text-[#3F3F3F] leading-relaxed mb-6">
          Tick every item below to publicly affirm that your kennel meets all {totalItems || 15} of Armstrong's seller standards. Your attestation is recorded under standards version <strong>{doc?.version || "—"}</strong> and is visible on your breeder card, every listing you create, and every escrow receipt.
        </p>

        {my?.attested && (
          <div className="rounded-2xl border border-[#0F8043]/30 bg-[#F1FAF3] p-4 mb-6 flex items-start gap-3" data-testid="already-attested-banner">
            <VerifiedStandardsBadge size="md" />
            <div className="flex-1 text-[13px] text-[#0F8043]">
              You're currently verified under version <strong>{my.attestation?.version}</strong> · attested {my.attestation?.attested_at?.split("T")[0]}.
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={remove}
              data-testid="remove-attestation"
              className="rounded-full border-[#C1272D] text-crimson h-8 px-3 text-[12px]"
            >
              <Trash size={12} className="mr-1" /> Remove badge
            </Button>
          </div>
        )}

        {loading && <RowListSkeleton />}

        {!loading && (
          <>
            <label className="block text-[12px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-2">
              Public display name (optional)
            </label>
            <Input
              data-testid="public-display-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Armstrong's Working Malinois"
              className="mb-6 max-w-md"
            />

            <div className="flex items-center justify-between gap-2 mb-3">
              <p className="text-[12px] text-[#6B6B6B]">
                <strong>{ack.size}</strong> / {totalItems} acknowledged
              </p>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={tickAll} data-testid="tick-all" className="rounded-full h-8 px-3 text-[12px]">
                  Tick all
                </Button>
                <Button size="sm" variant="outline" onClick={untickAll} data-testid="untick-all" className="rounded-full h-8 px-3 text-[12px]">
                  Clear
                </Button>
              </div>
            </div>

            {(doc?.sections || []).map((sec) => (
              <section key={sec.id} className="mb-7" data-testid={`attest-section-${sec.id}`}>
                <h2 className="font-spectral text-xl text-ink tracking-tight mb-3">{sec.title}</h2>
                <div className="space-y-2.5">
                  {(sec.items || []).map((it) => (
                    <label
                      key={it.id}
                      data-testid={`attest-item-${it.id}`}
                      className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                        ack.has(it.id)
                          ? "bg-[#F1FAF3] border-[#0F8043]/40"
                          : "bg-white border-[#E5E5E5] hover:border-[#C9A227]/50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={ack.has(it.id)}
                        onChange={() => toggle(it.id)}
                        className="mt-0.5 accent-[#0F8043] h-4 w-4"
                        data-testid={`attest-checkbox-${it.id}`}
                      />
                      <span
                        className="text-[13.5px] text-ink leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: (it.label || "").replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>"),
                        }}
                      />
                    </label>
                  ))}
                </div>
              </section>
            ))}

            <p className="text-[11px] text-[#6B6B6B] leading-relaxed mb-6 italic">
              {doc?.legal_disclaimer || ""}
            </p>
          </>
        )}

        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-[#E5E5E5] py-3 z-30">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-3">
            <Link to="/seller-standards" className="text-[13px] text-[#6B6B6B] hover:text-ink">
              ← Back to standards
            </Link>
            <Button
              size="lg"
              onClick={submit}
              disabled={!allChecked || submitting}
              data-testid="submit-attestation"
              className="rounded-full bg-[#0E0E0E] hover:bg-[#0F8043] text-white h-11 px-6"
            >
              <ShieldCheck size={16} weight="fill" className="mr-2" />
              {submitting ? "Saving…" : my?.attested ? "Update attestation" : `Confirm all ${totalItems} & earn badge`}
            </Button>
          </div>
        </div>
      </article>
    </AppShell>
  );
}
